import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
	Form,
	Input,
	Button,
	Modal,
	Dropdown,
	Menu,
	Row,
	Col,
	Table,
	Card,
	notification,
	Tag,
	Checkbox,
	Select,
} from "antd";
import { UnorderedListOutlined, FileTextOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Excel } from "antd-table-saveas-excel";
import infraChangeRequestService from "../../services/infraChangeRequest";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import "./index.css";
import { useMediaQuery } from "react-responsive";
import TableActionBtnsComponent from "../../components/tableActionsBtns";
import categoryService from "../../services/category";
import subCategoryService from "../../services/subCategory";

const { confirm } = Modal;

const IcrIndex = () => {
	const [searchForm] = Form.useForm();
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(15);
	const [mdmLoading, setMdmLoading] = useState(false);
	const [expandedRowKeys, setExpandedRowKeys] = useState([]);
	const [data, setData] = useState([]);
	const [visible, setVisible] = useState(false);
	const [formVisible, setFormVisible] = useState(false);
	const { currentUser } = useSelector((state) => state.user);
	const [categories, setCategories] = useState(null);
	const [subCategories, setSubCategories] = useState([]);
	const [formId, setFormId] = useState(10);
	const [searchValues, setSearchValues] = useState({});

	const allowedPermissions = currentUser?.["roles.permission"]?.map(
		(permission) => permission.name
	);

	const hasPermission = (permission) =>
		allowedPermissions?.includes(permission);


	// FOR STATE SAVE
	const depLocalKey = currentUser?.user_id
	? `icrLocal_${currentUser.user_id}`
	: null;
	const DEP_TTL_MS = 24 * 60 * 60 * 1000;

	const saveTableState = (state) => {
		if (!depLocalKey) return;
		localStorage.setItem(depLocalKey, JSON.stringify({ ...state, savedAt: Date.now() }));
	};

	const handleSubCategories = async () => {
		try {
			const response = await subCategoryService.getAllSubCategories();
			console.log(response);
			setSubCategories(response);
		} catch (error) {
			console.error("Error while fetching subCategories:", error);
		}
	};

	const getCategories = async () => {
		const response = await categoryService.getAllCategories();
		setCategories(response);
	};

	const toggleFormVisibility = () => {
		setFormVisible(!formVisible);
	};

	const onSearch = async (values) => {
		setSearchValues(values);

		saveTableState({
			searchValues: values,
			page: 1,
			perPage: itemsPerPage,
			sortBy: "",
			sortOrder: "",
		});

		setMdmLoading(true);

		try {
			const response = await infraChangeRequestService.search(values);

			setData(response.data);
			setCurrentPage(response.meta?.current_page || response.current_page);
			setTotalDataCount(response.meta?.total || response.total);
			setItemsPerPage(response.meta?.per_page || response.per_page);
		} catch (error) {
			console.error(error);
		} finally {
			setMdmLoading(false);
		}
	};

	const clearFilters = () => {
		searchForm.resetFields();
		setSearchValues({});
		setCurrentPage(1);

		if (depLocalKey) {
			localStorage.removeItem(depLocalKey);
		}

		getRecords(1, itemsPerPage, "", "");
	};

	useEffect(() => {
		getCategories();
		handleSubCategories();

		if (!depLocalKey) return;

		const saved = localStorage.getItem(depLocalKey);

		if (saved) {
			const parsed = JSON.parse(saved);
			if (parsed.savedAt && Date.now() - parsed.savedAt > DEP_TTL_MS) {
				localStorage.removeItem(depLocalKey);
				getRecords();
				return;
			}

			setSearchValues(parsed.searchValues || {});
			setCurrentPage(parsed.page || 1);
			setItemsPerPage(parsed.perPage || 15);

			// wait for form ready
			setTimeout(() => {
				searchForm.setFieldsValue(parsed.searchValues || {});
			}, 0);

			getRecords(
				parsed.page || 1,
				parsed.perPage || 15,
				parsed.sortBy || "",
				parsed.sortOrder || "",
				parsed.searchValues || {}
			);
		} else {
			getRecords();
		}
	}, [depLocalKey]);

	const columns = [
		{
			title: "Sequence No",
			dataIndex: "sequence_no",
			key: "sequence_no",
			width: 130,
			className: "resizable-column",
			sorter: true,
			fixed: useMediaQuery({ minWidth: 768 }) ? "left" : null,
		},
		{
			title: "Deployment Title",
			dataIndex: "request_title",
			key: "request_title",
			width: 160,
			className: "resizable-column",
			sorter: true,
			fixed: useMediaQuery({ minWidth: 768 }) ? "left" : null,
			render: (text) =>
				text && text.length > 40 ? `${text.slice(0, 40)}...` : text,
		},

		{
			title: "Affected Components",
			dataIndex: "affected_components",
			key: "affected_components",
			width: 200,
			sorter: true,
			render: (comps) => {
				if (!comps) return "N/A";
				let parsed = comps;
				if (typeof comps === "string") {
					try {
						parsed = JSON.parse(comps);
					} catch (e) {
						return comps;
					}
				}
				return Array.isArray(parsed) ? parsed.join(", ") : String(parsed);
			},
		},
		{
			title: "Specification",
			dataIndex: "change_request_specification",
			key: "change_request_specification",
			width: 180,
			render: (text) =>
				text && text.length > 40 ? `${text.slice(0, 40)}...` : text,
		},
		{
			title: "Old Values",
			dataIndex: "old_values",
			key: "old_values",
			width: 180,
			render: (text) =>
				text && text.length > 40 ? `${text.slice(0, 40)}...` : text,
		},
		{
			title: "New Values",
			dataIndex: "proposed_new_values",
			key: "proposed_new_values",
			width: 180,
			render: (text) =>
				text && text.length > 40 ? `${text.slice(0, 40)}...` : text,
		},

		{
			title: "Location",
			dataIndex: ["location", "name"],
			key: "location",
			width: 120,
			sorter: true,
			render: (text, record) => record?.location?.name || "N/A", // Optional chaining
		},

		{
			title: "Created At",
			dataIndex: "created_at",
			key: "created_at",
			width: 120,
			sorter: true,
		},

		{
			title: "Status",
			dataIndex: "status",
			key: "scrfStatus",
			sorter: true,
			width: 100,
			render: (status) => (
				<Tag
					color={
						(status === "Pending" && "orange") ||
						(status === "Approved" && "green") ||
						(status === "Return" && "red")
					}
				>
					{status}
				</Tag>
			),
			fixed: useMediaQuery({ minWidth: 768 }) ? "right" : null,
		},
		// {
		//   title: 'Actions',
		//   key: 'Actions',
		//   width: 40,
		//   sorter: true,
		//   fixed: useMediaQuery({ minWidth: 768 }) ? 'right' : null,
		//   render: (text, record) => record && ( // Check if record exists
		//     <TableActionBtnsComponent
		//       record={record}
		//       handleDelete={handleDelete}
		//       currentUser={currentUser}
		//       formId={formId}

		//     />
		//   ),
		// },
		{
			title: "Actions",
			dataIndex: "Actions",
			key: "Actions",
			width: 120, // wider for spacing
			fixed: "right",
			render: (text, record) => (
				<TableActionBtnsComponent
					record={record}
					handleDelete={handleDelete}
					currentUser={currentUser}
					formId={formId}
				/>
			),
		},
	];

	const getRecords = async (
		page = 1,
		itemsPerPage = 15,
		sortBy = "",
		sortOrder = "",
		searchParams = null
	) => {
		setMdmLoading(true);
		try {
			let response;
			if (searchParams && Object.keys(searchParams).length > 0) {
				response = await infraChangeRequestService.search({
					...searchParams,
					page,
					itemsPerPage,
					sortBy,
					sortOrder,
				});
			} else {
				response = await infraChangeRequestService.getAll(
					page,
					itemsPerPage,
					sortBy,
					sortOrder
				);
			}

			console.log(response);
			setData(response.data);
			setCurrentPage(response.meta?.current_page || response.current_page);
			setTotalDataCount(response.meta?.total || response.total);
			setItemsPerPage(response.meta?.per_page || response.per_page);
		} catch (error) {
			console.error("Failed to fetch data:", error);
		} finally {
			setMdmLoading(false);
		}
	};

	// const handleClick = () => {
	// 	const excelColumns = columns
	// 		.filter((column) => column.key !== "Actions")
	// 		.map((column) => {
	// 			const { render, ...columnWithoutRender } = column;
	// 			return columnWithoutRender;
	// 		});

	// 	const excel = new Excel();
	// 	excel
	// 		.addSheet("data")
	// 		.addColumns(excelColumns)
	// 		.addDataSource(data, {
	// 			str2Percent: true,
	// 		})
	// 		.saveAs("Excel.xlsx");
	// };

	const handleExport = async () => {
		try {
			setMdmLoading(true);

			let response;

			// ✅ If filters applied, use search API
			if (searchValues && Object.keys(searchValues).length > 0) {
				response = await infraChangeRequestService.search({
					...searchValues,
					page: 1,
					perPage: 1000,
				});
			} else {
				// ✅ Otherwise normal API
				response = await infraChangeRequestService.getAll(1, 1000, "", "");
			}

			const exportData = response.data;

			// ✅ 🔥 Flatten data
			const formattedData = exportData.map((item) => ({
				...item,

				// objects → string
				location: item.location?.name || "",
			}));

			// ✅ Fix columns (remove render + fix nested dataIndex)
			const excelColumns = columns
				.filter((column) => column.key !== "Actions")
				.map(({ render, dataIndex, ...col }) => ({
					...col,
					dataIndex: Array.isArray(dataIndex) ? dataIndex[0] : dataIndex,
				}));

			const excel = new Excel();
			excel
				.addSheet("ICR")
				.addColumns(excelColumns)
				.addDataSource(formattedData)
				.saveAs("ICR_Export.xlsx");
		} catch (e) {
			console.error(e);
		} finally {
			setMdmLoading(false);
		}
	};

	const [visibleColumns, setVisibleColumns] = useState(() =>
		columns.map((column) => column.key)
	);

	const handleColumnToggle = (key) => {
		const updatedVisibleColumns = [...visibleColumns];

		if (visibleColumns.includes(key)) {
			const index = updatedVisibleColumns.indexOf(key);
			if (index > -1) {
				updatedVisibleColumns.splice(index, 1);
			}
		} else {
			updatedVisibleColumns.push(key);
		}

		setVisibleColumns(updatedVisibleColumns);

		try {
			localStorage.setItem(
				"visibleColumns",
				JSON.stringify(updatedVisibleColumns)
			);
		} catch (error) {
			console.error("Error saving visibleColumns to local storage:", error);
		}
	};

	const handleDelete = (record) => {
		confirm({
			title: "Are you sure you want to delete this record?",
			content: "This action cannot be undone.",
			okText: "Yes",
			okType: "danger",
			cancelText: "No",
			onOk: async () => {
				try {
					const response = await infraChangeRequestService.delete(record.id);
					if (response.success) {
						notification.success({
							message: "Deleted Successfully",
							description: response.message,
						});
						await getRecords(currentPage);
					}
				} catch (error) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				}
			},
			onCancel() {
				console.log("Delete action cancelled");
			},
		});
	};



	useEffect(() => {
		try {
			const savedVisibleColumns = localStorage.getItem("visibleColumns");
			if (savedVisibleColumns) {
				setVisibleColumns(JSON.parse(savedVisibleColumns));
			}
		} catch (error) {
			console.error("Error loading visibleColumns from local storage:", error);
		}
	}, []);

	const expandedRowRender = (records) => {
		const expandedData = Array.isArray(records.deploymentDetail)
			? records.deploymentDetail.map((record, index) => ({
					key: record.id || index.toString(),
					document_no: record.document_no,
					detail: record.detail,
			  }))
			: [];

		const expandedColumns = [
			{
				title: "Document No",
				dataIndex: "document_no",
				key: "document_no",
				width: 180,
			},
			{
				title: "Detail",
				dataIndex: "detail",
				key: "detail",
			},
		];

		return (
			<>
				<Table
					columns={expandedColumns}
					pagination={false}
					dataSource={expandedData}
					style={{ borderLeft: "2px solid #1599fd" }}
				/>
			</>
		);
	};

	const menuItems = columns.map((column) => ({
		key: column.key,
		label: (
			<Checkbox
				className='mr-5'
				style={{ fontSize: "12px" }}
				onChange={(e) => {
					e.stopPropagation();
					handleColumnToggle(column.key);
				}}
				checked={visibleColumns.includes(column.key)}
			>
				{column.title}
			</Checkbox>
		),
	}));

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header
						icon={<FileTextOutlined />}
						title='Infrastructure Change Request Form'
						right={
							<>
								 {/* {hasPermission("OwnWorkflow-view") && (
									<Link to={`/own-workflow?form_id=3`}>
									<Button className='mr-10'>
										My Workflows
									</Button>
									</Link>
								)}  */}

								<Button onClick={handleExport} className='mr-10'>
									Export
								</Button>

								<Link to={`/infrastructure-change-request/add/`}>
									<Button className='btn-blue' type='primary'>
										Add Infrastructure Change Request Form
									</Button>
								</Link>
							</>
						}
					/>
				</Col>
			</Row>

			<Card>
				<Button onClick={toggleFormVisibility}>
					{formVisible ? "Hide Search Form" : "Show Search Form"}
				</Button>

				{formVisible && (
					<Form form={searchForm} onFinish={onSearch} layout='vertical'>
						<Card className='pt-6' style={{ paddingBottom: "0 !important" }}>
							<Row gutter={[12, 12]}>
								{[
									{ key: "sequence_no", placeholder: "Sequence No" },
									{ key: "request_title", placeholder: "Request Title" },
									{ key: "affected_components", placeholder: "Affected Components" },
									{ key: "change_request_specification", placeholder: "Specification" },
									{ key: "old_values", placeholder: "Old Values" },
									{ key: "proposed_new_values", placeholder: "New Values" },
									{ key: "change_tested", placeholder: "Change Tested" },
									{ key: "roll_back_scenario", placeholder: "Rollback Scenario" },
									{ key: "request_live_and_functional", placeholder: "Live & Functional" },
								].map(({ key, placeholder }) => (
									<Col key={key} lg={6} md={12} sm={24} xs={24}>
										<Form.Item name={key}>
											<Input size='large' placeholder={`${placeholder}`} />
										</Form.Item>
									</Col>
								))}

								{/* <Col lg={6} md={12} sm={24} xs={24}>
                  <Form.Item name="software_category">
                    <Select
                      showSearch={true}
                      optionFilterProp="children"
                      style={{ width: "100%" }}
                      size="large"
                      placeholder="Please Select Category"
                      allowClear
                    >
                      {categories &&
                        categories.map((category) => (
                          <Select.Option
                            key={category.id}
                            value={category.name}
                          >
                            {category.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col> */}

								{/* <Col lg={6} md={12} sm={24} xs={24}>
                  <Form.Item name="software_subcategories">
                    <Select
                      showSearch={true}
                      optionFilterProp="children"
                      placeholder="Subcategory"
                      allowClear={true}
                      size="large"
                    >
                      {subCategories.map((subCategory) => (
                        <Select.Option
                          key={subCategory.id}
                          value={subCategory.name}
                        >
                          {subCategory.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col> */}
								<Col lg={24} md={24} sm={24} xs={24}>
									<div style={{ display: "flex", gap: "10px" }}>
										<Button
											block
											className='btn-blue'
											htmlType='submit'
											type='primary'
										>
											Search
										</Button>
										<Button
											block
											onClick={clearFilters}
										>
											Clear
										</Button>
									</div>
								</Col>
							</Row>
						</Card>
					</Form>
				)}
			</Card>

			<Row>
				<Col span={24}>
					<Card>
						<Dropdown
							className='mr-10'
							menu={{ items: menuItems }}
							open={visible}
							onOpenChange={(isVisible) => setVisible(isVisible)}
							trigger={["click"]}
						>
							<button // Replaced <a> with <button>
								style={{
									color: "black",
									fontSize: "11px",
									color: "gray",
									background: "transparent", // Make button background transparent
									border: "none", // Remove button border
									padding: 0, // Remove default button padding
									cursor: "pointer", // Set cursor to pointer
								}}
								onClick={(e) => e.preventDefault()}
							>
								<UnorderedListOutlined /> Columns
							</button>
						</Dropdown>

						<Table
							size='small'
							columns={columns.filter((column) =>
								visibleColumns.includes(column.key)
							)}
							style={{ minHeight: "100vh" }}
							dataSource={data.map((item) => ({ ...item, key: item.id }))}
							pagination={{
								showSizeChanger: true,
								showQuickJumper: true,
								showTotal: (total, range) =>
									`${range[0]}-${range[1]} of ${total} items`,
								current: currentPage,
								total: totalDataCount,
								pageSize: itemsPerPage,
								pageSizeOptions: ["10", "15", "25", "50", "100"],
							}}
							loading={mdmLoading}
							scroll={{
								x: 1400,
								y: "calc(100vh - 300px)",
							}}
							sticky={true}
							expandable={{
								expandedRowRender,
								onExpand: (expanded, record) => {
									if (expanded) {
										setExpandedRowKeys((prevKeys) => [...prevKeys, record.key]);
									} else {
										setExpandedRowKeys((prevKeys) =>
											prevKeys.filter((key) => key !== record.key)
										);
									}
								},
							}}
							onChange={(pagination, filters, sorter) => {
								const { order, field } = sorter;
								const sortOrder =
									order === "ascend"
										? "asc"
										: order === "descend"
										? "desc"
										: "";

								// ✅ save state
								saveTableState({
									searchValues,
									page: pagination.current,
									perPage: pagination.pageSize,
									sortBy: field,
									sortOrder,
								});

								getRecords(
									pagination.current,
									pagination.pageSize,
									field,
									sortOrder,
									searchValues
								);
							}}
						/>
					</Card>
				</Col>
			</Row>
		</DefaultLayout>
	);
};

export default IcrIndex;
